/**
 * PortfolioSection Component
 *
 * Component for displaying and managing portfolio items
 */

'use client';

import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { SecondaryButton } from '@/src/shared/ui/button';
import { buildStaticUrl } from '@/src/shared/api';
import type { PortfolioItem } from '../../../api';

export interface PortfolioSectionProps {
  portfolioData?: PortfolioItem[];
  onDelete: (id: string) => Promise<void>;
  isDeleting: boolean;
  onAddPhoto?: () => void;
}

export function PortfolioSection({
  portfolioData,
  onDelete,
  isDeleting,
  onAddPhoto,
}: PortfolioSectionProps) {
  const handleDelete = async (item: PortfolioItem) => {
    if (confirm('Are you sure you want to delete this portfolio item?')) {
      try {
        await onDelete(item.id);
      } catch (error) {
        console.error('Failed to delete portfolio item:', error);
        alert('Failed to delete portfolio item');
      }
    }
  };

  return (
    <div>
      <Typography variant="h3" className="mb-4">
        Portfolio
      </Typography>

      {portfolioData && portfolioData.length > 0 ? (
        <div className="space-y-4 mb-4">
          {portfolioData.map((item) => (
            <div
              key={item.id}
              className="flex items-start gap-4 p-4 border border-border-primary rounded-2xl"
            >
              {item.images && item.images.length > 0 && (
                <img
                  src={buildStaticUrl(item.images[0])}
                  alt={item.title}
                  className="w-24 h-24 object-cover rounded-xl"
                />
              )}
              <div className="flex-1">
                <Typography variant="body" weight="semibold">
                  {item.title}
                </Typography>
                {item.description && (
                  <Typography variant="bodySm" color="secondary" className="mt-1">
                    {item.description}
                  </Typography>
                )}
                <Typography variant="bodySm" color="secondary" className="mt-1">
                  {item.images?.length || 0} image(s)
                </Typography>
              </div>
              <button
                onClick={() => handleDelete(item)}
                disabled={isDeleting}
                className="p-3 hover:bg-background-secondary rounded-full transition-colors disabled:opacity-50"
              >
                <Icon name="x" size="sm" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 border border-border-primary rounded-2xl bg-background-secondary mb-4">
          <Typography variant="body" color="secondary" className="text-center">
            No portfolio items yet. Add your first work sample!
          </Typography>
        </div>
      )}

      {/* Add Photo Button */}
      {onAddPhoto && (
        <div className="flex justify-center">
          <SecondaryButton
            onClick={onAddPhoto}
            className="border-accent-primary text-accent-primary hover:bg-accent-primary/5"
          >
            <Icon name="plus" size="sm" className="mr-2" />
            Add Photo
          </SecondaryButton>
        </div>
      )}
    </div>
  );
}
